import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from '@firebase/firestore';
import { captureException as sentryCaptureException, captureMessage as sentryCaptureMessage } from '@sentry/react';

import PaymentContext from '../PaymentContext';
import TillPaymentForm from './TillPaymentForm';
import { useAnalytics, useAuth } from '../../../hooks';
import { scriptPriceTranslator, getCatalogueId } from '../../../utils';
import { getPaymentSuccessUrl } from '../paymentHelpers';
import { db, functions } from '../../../firebase-config';

/**
 * @type {import('firebase/functions').HttpsCallable<
 *  TillOrderBody | TillCustomOrderBody,
 *  CloudFunctionGenericReturnDataType & { orderId: string, orderResponse: TillApiResponse, amountPaid?: number },
 * >}
 */
const tillCreateOrder = httpsCallable(functions, 'tillCreateOrder_v2');

const BORDER_RED = '1px solid #d32f2f';

const WEBHOOK_CONFIRMATION_TIMEOUT_MS = 60_000;

const getOrderBody = ({
  isScriptMode,
  selectedTreatments,
  formName,
  promoCode,
  isPickup,
  transactionToken,
  pickupPharmacyId,
}) => {
  const priceIds = isScriptMode ? [scriptPriceTranslator[formName]] : selectedTreatments;
  const cleanedProductIds = priceIds.map((id) => getCatalogueId(id));
  const orderRequestedAt = Date.now();

  return {
    updated: orderRequestedAt,
    productIds: cleanedProductIds,
    discountCode: promoCode,
    orderRequestedAt,
    isPickup,
    formName,
    transactionToken,
    pickupPharmacyId,
  };
};

const handleTillPaymentFormSubmitError = ({ error, paymentData, setErrorMessage, user }) => {
  sentryCaptureException(error, {
    extra: { userId: user?.uid, issueIn: 'TillCreateOrder', paymentData },
  });
  setErrorMessage(error.toString());
};

/**
 * TillPayment component handles the payment process using Till Payments.
 *
 * @component
 * @returns {JSX.Element} The TillPayment component.
 */
const TillPayment = () => {
  const analytics = useAnalytics();
  const { user } = useAuth();
  const {
    selectedTreatments,
    isPickup,
    scriptMode: isScriptMode,
    formName,
    promoCode,
    reference: customPaymentReference,
    selectedPharmacy,
  } = useContext(PaymentContext);
  const [paymentJs, setPaymentJs] = useState(null);
  const [orderDocId, setOrderDocId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPaymentSucceeded, setHasPaymentSucceeded] = useState(false);
  const [isWebhookTimedOut, setIsWebhookTimedOut] = useState(false);
  const paymentInFlightRef = useRef(false);
  const [isPaymentJsLoading, setIsPaymentJsLoading] = useState(true);
  const [isPaymentJsLoadFailed, setIsPaymentJsLoadFailed] = useState(false);
  const [cardType, setCardType] = useState(null);

  // Form fields
  const [cardHolderName, setCardHolderName] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  // Form errors
  const [cardHolderNameError, setCardHolderNameError] = useState('');
  const [monthError, setMonthError] = useState('');
  const [yearError, setYearError] = useState('');
  const [cvvError, setCvvError] = useState('');
  const [cardNumberError, setCardNumberError] = useState('');

  const navigate = useNavigate();

  const onPaymentJsInit = useCallback((payment) => {
    const isCardNumberFocused = false;
    const isCvvFocused = false;

    // Autofill Payment
    payment.enableAutofill();
    payment.onAutofill((data) => {
      setCardHolderName(data?.card_holder);
      setMonth(data?.month);
      setYear(data?.year);
    });

    // Styles
    const numberCvvStyles = {
      height: '2.5rem',
      padding: '10px',
      background: 'transparent',
      borderColor: '#0000003b',
      borderWidth: '1px',
      borderRadius: '5px',
      fontSize: '16px',
      fontFamily: 'Roboto !important',
      fontWeight: '400 !important',
      color: '#303F56 !important',
    };

    const borderFocusStyle = {
      border: '2px solid #2aafbb',
      outline: 'unset',
    };

    const borderHoverStyle = {
      borderColor: '0000003b',
    };

    payment.setCvvStyle({
      'text-align': 'right',
    });

    payment.setNumberStyle(numberCvvStyles);
    payment.setCvvStyle(numberCvvStyles);

    // Focus event listeners
    payment.numberOn('focus', () => {
      payment.setNumberStyle(borderFocusStyle);
    });

    payment.numberOn('blur', () => {
      payment.setNumberStyle({ border: '1px solid #0000003b' });
    });

    payment.cvvOn('focus', () => {
      payment.setCvvStyle(borderFocusStyle);
    });

    payment.cvvOn('blur', () => {
      payment.setCvvStyle({ border: '1px solid #0000003b' });
    });

    // Hover events
    payment.numberOn('mouseover', () => {
      if (!isCardNumberFocused) {
        return;
      }
      payment.setNumberStyle(borderHoverStyle);
    });

    payment.numberOn('mouseout', () => {
      if (!isCardNumberFocused) {
        return;
      }
      payment.setNumberStyle(numberCvvStyles);
    });

    payment.cvvOn('mouseover', () => {
      if (!isCvvFocused) {
        return;
      }
      payment.setCvvStyle(borderHoverStyle);
    });

    payment.cvvOn('mouseout', () => {
      if (!isCvvFocused) {
        return;
      }
      payment.setCvvStyle(numberCvvStyles);
    });

    // Input event listeners
    payment.numberOn('input', (data) => {
      setCardType(data.cardType || null);
      setCardNumberError('');

      // Throw an error if the card is a valid amex card
      if (data.cardType === 'amex' && data.validNumber) {
        payment.setNumberStyle({ border: BORDER_RED });
        setCardNumberError('Sorry, we only accept Visa and Mastercard.');
        return;
      }

      if (data.validNumber) {
        payment.setNumberStyle(borderFocusStyle);
        setCardNumberError('');
      } else {
        payment.setNumberStyle({ border: BORDER_RED });
      }
    });

    payment.cvvOn('input', (data) => {
      setCvvError('');
      if (data.validCvv) {
        payment.setCvvStyle(borderFocusStyle);
        setCvvError('');
      } else {
        payment.setCvvStyle({ border: BORDER_RED });
      }
    });

    setPaymentJs(payment);
    setIsPaymentJsLoading(false);
  }, []);

  const initializePaymentForm = useCallback(async () => {
    const paymentJSsrc = import.meta.env.VITE_APP_TILL_PAYMENTS_JS_BUNDLE_URL;
    const tillPublicKey = import.meta.env.VITE_APP_TILL_PAYMENTS_PUBLIC_INTEGRATION_KEY;
    const script = document.createElement('script');
    script.setAttribute('src', paymentJSsrc);
    script.setAttribute('data-main', 'payment-js');
    script.onload = () => {
      try {
        const newPaymentJs = new window.PaymentJs();
        newPaymentJs.init(tillPublicKey, 'number_div', 'cvv_div', onPaymentJsInit);
        setPaymentJs(newPaymentJs);
      } catch (error) {
        setIsPaymentJsLoadFailed(true);
        sentryCaptureException(error, {
          extra: { userId: user?.uid, issueIn: 'Issue in initializing Till PaymentJs script', formName },
        });
      }
    };

    // If script fails to load
    // Update this in the future if patients are having issues with the script and are taking too long to load
    script.onerror = () => {
      setIsPaymentJsLoading(false);
      setIsPaymentJsLoadFailed(true);
      sentryCaptureMessage('Failed to load Till PaymentJs script', {
        level: 'warning',
        extra: { userId: user?.uid, formName },
      });
    };

    document.body.appendChild(script);
  }, [onPaymentJsInit, user?.uid, formName]);

  const handleTillPaymentErrors = (errors = []) => {
    if (!errors.length) {
      return;
    }

    toast.error('An error occurred', {
      description: errors.map((error) => error.message).join('. '),
    });

    errors.forEach((errorObj) => {
      switch (errorObj.attribute) {
        case 'card_holder':
          setCardHolderNameError(errorObj.message);
          break;
        case 'number':
          paymentJs.setNumberStyle({ border: BORDER_RED });
          setCardNumberError(errorObj.message);
          break;
        case 'month':
          setMonthError(errorObj.message);
          break;
        case 'year':
          setYearError(errorObj.message);
          break;
        case 'cvv':
          paymentJs.setCvvStyle({ border: BORDER_RED });
          setCvvError(errorObj.message);
          break;
        default:
          break;
      }
    });
  };

  const handlePaymentFormSubmit = async () => {
    // ref blocks double-clicks synchronously; state is a fallback if the ref was somehow reset mid-flight.
    if (paymentInFlightRef.current) return;
    if (hasPaymentSucceeded) return;
    paymentInFlightRef.current = true;
    setIsWebhookTimedOut(false);

    const paymentData = {
      card_holder: cardHolderName,
      month,
      year,
    };

    try {
      setErrorMessage('');
      setIsSubmitting(true);
      const transactionToken = await new Promise((resolve) => {
        paymentJs.tokenize(
          paymentData,
          (token) => {
            resolve(token);
          },
          (errors) => {
            handleTillPaymentErrors(errors);
            resolve(null);
          },
        );
      });

      // Tokenization failed — field errors already set by handleTillPaymentErrors.
      if (!transactionToken) {
        paymentInFlightRef.current = false;
        setIsSubmitting(false);
        return;
      }

      const orderBody = customPaymentReference
        ? { transactionToken, customPaymentReference }
        : getOrderBody({
            isScriptMode,
            selectedTreatments,
            formName,
            promoCode,
            isPickup,
            transactionToken,
            pickupPharmacyId: selectedPharmacy,
          });

      const healthCategory = formName || undefined;

      analytics.capture('checkout_flow:create_order_start', {
        promoCode,
        healthCategory,
        $groups: { healthCategory },
      });

      // Create order request
      const { data: responseData } = await tillCreateOrder(orderBody);
      const { orderId, orderResponse, amountPaid, error } = responseData;

      analytics.capture('checkout_flow:create_order_generate', {
        orderId,
        orderResponse,
        healthCategory,
        $groups: { healthCategory },
      });

      if (!orderResponse) {
        throw new Error(
          error || 'An unknown error occurred. Please try again later. Contact support if the problem persists.',
        );
      }

      if (orderResponse.errors) {
        const {
          code,
          message,
          adapterCode,
          adapterMessage,
          errorCode,
          errorMessage: errorMessageFromTill,
        } = orderResponse.errors[0];

        // Till Payments does not send the error codes and error messages properly as described in the API documentation so we will try to check all the possible error codes and messages from the payload
        // https://gateway.tillpayments.com/documentation/gateway#error-codes
        const tillErrorCode = parseInt(errorCode || adapterCode || code, 10);
        const tillErrorMessage = [message, adapterMessage, errorMessageFromTill].filter(Boolean).join('. ');

        if (tillErrorCode === 1002 && adapterMessage === 'invalid or missing parameter') {
          setCardHolderNameError('Please double check your cardholder name.');
        }

        throw new Error(`${tillErrorMessage}. Error Code: ${tillErrorCode}. Please try again.`);
      }

      // Note: this is after orderResponse.errors as a catch all -- need to speak with the rest of the team cc @cfnelson
      if (error) {
        throw new Error(error);
      }

      // This is just a temporary if. Really we should fail the submission if healthCategory is not defined.
      // Really healthCategory should actually always need to be defined. The jsdoc types are wrong :(
      // CD-768: value - The final amount charged in AUD dollars, derived from amountPaid returned by tillCreateOrder_v2. Only included if valid.
      if (healthCategory) {
        analytics.capture('checkout_flow:create_order_success', {
          orderId,
          healthCategory,
          ...(Number.isFinite(amountPaid) && { value: amountPaid }),
          $set: {
            formsPaid: [{ healthCategory, paidAt: new Date().toISOString() }],
          },
          $groups: { healthCategory },
        });
      }

      // Keep paymentInFlightRef true — prevents re-submission while waiting for the webhook to navigate the user away.
      setHasPaymentSucceeded(true);
      setOrderDocId(orderId);
    } catch (error) {
      handleTillPaymentFormSubmitError({ error, paymentData, setErrorMessage, user });
      setIsSubmitting(false);
      paymentInFlightRef.current = false;
    }
  };

  const handleInput = (e, field) => {
    const { value } = e.target;
    switch (field) {
      case 'cardHolderName':
        setCardHolderName(value);
        setCardHolderNameError('');
        break;
      case 'month':
        setMonth(value);
        setMonthError('');
        break;
      case 'year':
        setYear(value);
        setYearError('');
        break;
      default:
        break;
    }
  };

  // Show a fallback if the Till webhook confirmation doesn't arrive in time.
  useEffect(() => {
    if (!hasPaymentSucceeded) return undefined;

    const timeoutId = setTimeout(() => {
      setIsWebhookTimedOut(true);
    }, WEBHOOK_CONFIRMATION_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [hasPaymentSucceeded]);

  useEffect(() => {
    initializePaymentForm();
  }, [initializePaymentForm]);

  useEffect(() => {
    try {
      if (!user) {
        return () => {};
      }

      if (!orderDocId) {
        return () => {};
      }
      // Subscribe to the document for changes
      return onSnapshot(doc(db, 'patients', user.uid, 'tillOrderAttempts', orderDocId), (docSnapshot) => {
        const data = docSnapshot.data();
        setErrorMessage('');
        if (data?.webhook?.result === 'OK') {
          const paymentSuccessRoute = getPaymentSuccessUrl({ isScriptMode, formName, productIds: selectedTreatments });
          navigate(paymentSuccessRoute);
        }
        if (data?.webhook?.result === 'ERROR') {
          const tillErrorMessage = data?.webhook?.message || 'Payment was not successful.';
          const tillAdapterMessage = data?.webhook?.adapterMessage ? ` (${data?.webhook?.adapterMessage})` : '';

          if (data?.webhook?.errorCode === 1002 && data?.webhook?.adapterMessage === 'invalid or missing parameter') {
            setCardHolderNameError('Please double check the card holder name.');
          }

          setErrorMessage(`${tillErrorMessage}${tillAdapterMessage}. Please try again.`);
          setHasPaymentSucceeded(false);
          setIsWebhookTimedOut(false);
          setIsSubmitting(false);
          paymentInFlightRef.current = false;
          sentryCaptureException(new Error(`Till webhook error: ${tillErrorMessage}${tillAdapterMessage}`), {
            extra: { userId: user?.uid, orderDocId: docSnapshot.ref.id },
          });
        }
      });
    } catch (error) {
      sentryCaptureException(error, {
        extra: { userId: user?.uid, orderDocId },
      });
      setErrorMessage(error.toString());
      setIsPaymentJsLoading(false);
      return () => {};
    }
  }, [formName, isPickup, isScriptMode, promoCode, selectedTreatments, user, orderDocId, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        height: '100%',
        p: '20px',
        gap: '10px',
      }}
    >
      <TillPaymentForm
        isSubmitting={isSubmitting}
        hasPaymentSucceeded={hasPaymentSucceeded}
        isWebhookTimedOut={isWebhookTimedOut}
        handlePaymentFormSubmit={handlePaymentFormSubmit}
        cardHolderName={cardHolderName}
        handleInput={handleInput}
        month={month}
        year={year}
        cardType={cardType}
        cardHolderNameError={cardHolderNameError}
        monthError={monthError}
        yearError={yearError}
        cvvError={cvvError}
        cardNumberError={cardNumberError}
        errorMessage={errorMessage}
        isPaymentJsLoading={isPaymentJsLoading}
        isPaymentJsLoadFailed={isPaymentJsLoadFailed}
        paymentJs={paymentJs}
      />
    </Box>
  );
};

export default TillPayment;
